import React, {useContext, useState} from 'react';
import {BoShowContext} from "../context/bo-show-context.jsx";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import projectUtils from "../../../../../utils/project-utils.js";
import {BoKeys} from "../../resources/bo-keys.js";
import {FaExternalLinkAlt, FaHistory} from "react-icons/fa";
import BoShowAttachmentGallery from "./bo-show-attachment-gallery.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {navigateHook} from "../../../../../hooks/use-navigate.js";

const formatIndividualVersion = (value) => (value ?? "").replace(/<br\s*\/?>/gi, "\n");

const BoShowIndividuals = () => {
    const {boData, verifySyncUrl, urlAttach, printOptions, individualShowData, setIndividualShowData, attachesToHide, handleAttachToHide, setModalAttach} = useContext(BoShowContext);
    const navigate = navigateHook();

    const individualList = boData.individualList;

    const [modalVersion, setModalVersion] = useState({open: false, index: undefined})

    const handleShowData = (id) => setIndividualShowData(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

    const openModal = (relatedPhotoFace) => {
        if (!relatedPhotoFace) return;

        setModalAttach({open: true, attach: relatedPhotoFace})
    }

    return (
        <>
            {
                individualList?.map((individual, index) => {
					const hasLegacyTattoo = typeof individual.tattoo === "string" && !!individual.tattoo.trim();
					const individualRegions = Array.isArray(individual.individualRegionWrapper)
						? [...individual.individualRegionWrapper]
						: [];
					const hasEquivalentTattoo = individualRegions.some((region) =>
						region?.type === "TATUAGEM" && region?.description === individual.tattoo);
					if (hasLegacyTattoo && !hasEquivalentTattoo) {
						const biggestItemId = individualRegions.reduce((largest, region) => {
							const itemId = Number.parseInt(region?.itemId, 10);
							return Number.isFinite(itemId) ? Math.max(largest, itemId) : largest;
						}, 0);
						individualRegions.push({
							itemId: String(biggestItemId + 1),
							local: null,
							type: "TATUAGEM",
							description: individual.tattoo,
						});
					}

                    let version = '';
                    let signOption = '';
                    let signatureId = '';
                    let biggestItemId = 0;

                    if (individual.individualVersion && Array.isArray(individual.individualVersion) && individual.individualVersion.length) {
                        biggestItemId = individual.individualVersion.length - 1;

                        const currentVersion = individual.individualVersion[biggestItemId];
                        version = formatIndividualVersion(currentVersion.version);
                        if(currentVersion.nothingToDeclare) {
                            version = 'Nada a declarar';
                        }

                        signOption = currentVersion.signOption ?? '';
                        signatureId = currentVersion.signatureId ?? '';
                    }

                    const individualDocument = individual?.documentPhotoId
                        ? individual?.attachs?.find((attach) => attach?.attachId === individual.documentPhotoId)
                        : undefined;
                    const relatedPhotoFace = individual?.attachs?.find((attach) => attach?.attachId === individual?.relatedPhotoFaceId);
                    const specialAttachIds = new Set([
                        individual.relatedPhotoFaceId,
                        individual.documentPhotoId,
                        ...(individual.individualVersion ?? []).map((version) => version.signatureId),
                        ...(individual.articles ?? []).map((article) => article.attachId),
                    ].filter((attachId) => attachId !== null && attachId !== undefined));
                    const generalAttaches = (individual.attachs ?? []).filter((attach) => !specialAttachIds.has(attach.attachId));
                    const photoAttaches = individualDocument
                        ? [...generalAttaches, {...individualDocument, description: "Foto do documento"}]
                        : generalAttaches;
                    if (relatedPhotoFace) {
                        relatedPhotoFace.url = verifySyncUrl(individual.relatedPhotoFaceId) + "/" + individual.relatedPhotoFaceId
                    }

                    const isRequester = individual?.conditions?.some(c => c === "Solicitante");
                    const showData = individualShowData.includes(individual.itemId);

                    const hideData = !showData && printOptions.individual;

                    return (
                        <article key={"individual-" + index} className={"space-y-4" + (isRequester && !printOptions.requester ? " print:hidden" : "")}>
                            <h2 className={"resume-title relative flex gap-2 justify-center items-center"}>
                                INDIVÍDUO - { index + 1 }
                                {
                                    individual.cpf &&
                                    <span className={"cursor-pointer print:hidden"} onClick={() => navigate(`/individuals/form/${individual.cpf}`, true)}>
                                        <FaExternalLinkAlt size={14}/>
                                    </span>
                                }

                                {
                                    printOptions.individual &&
                                    <InputCheckbox
                                        id={"individual-check-" + index}
                                        checked={showData}
                                        label={"Exibir dados"}
                                        onChange={() => handleShowData(individual.itemId)}
                                        className={"absolute top-[5px] right-[10px] print:hidden"}
                                    />
                                }
                            </h2>

                            <section className={"grid grid-cols-12 gap-3"}>
                                <div className={"col-span-4"}>
                                    <div className={"relative image max-h-[225px]"}>
                                        {
                                            printOptions?.image &&
                                            <InputCheckbox
                                                id={"attach-check-" + individual.relatedPhotoFaceId}
                                                checked={!attachesToHide.includes(individual.relatedPhotoFaceId)}
                                                label={"Exibir"}
                                                onChange={() => handleAttachToHide(individual.relatedPhotoFaceId)}
                                                className={"absolute top-[5px] left-[10px] print:hidden"}
                                            />
                                        }

                                        <img
                                            className={(printOptions?.image && !attachesToHide.includes(individual.relatedPhotoFaceId) ? "print:block" : " print:hidden")}
                                            src={individual.relatedPhotoFaceId ? verifySyncUrl(individual.relatedPhotoFaceId) + "/" + individual.relatedPhotoFaceId : getAssetPath("img/user.png")}
                                            alt=""
                                            onClick={() => openModal(relatedPhotoFace)}
                                        />
                                        <img
                                            className={"hidden" + (!printOptions?.image || attachesToHide.includes(individual.relatedPhotoFaceId) ? " print:block" : "")}
                                            src={getAssetPath("img/user.png")}
                                            alt=""
                                        />
                                    </div>
                                </div>
                                <div className={"col-span-8 grid grid-cols-2 gap-2 self-start"}>
                                    <p>CPF: <span className={"info !font-bold"}>{projectUtils.formatCpfToView(individual.cpf)}</span></p>
                                    <p>Nome: <span className={"info"}>{individual.name ?? ""}</span></p>
                                    <p>Nome social: <span className={"info"}>{individual.socialName ?? ''}</span></p>
                                    <p>Apelido: <span className={"info"}>{individual.nickname ?? ""}</span></p>
                                    <p>Sexo: <span className={"info"}>{individual.sex ? (BoKeys[individual.sex] ?? individual.sex) : ''}</span></p>
                                    <p>Data de nascimento: <span className={"info"}>{individual.dtBirth ?? ""}</span></p>
                                    <p>Condições: <span className={"info"}>{individual.conditions?.join(", ")}</span></p>
                                    <p>Encaminhamentos: <span className={"info"}>{individual.referral ?? ""}</span></p>
                                </div>
                            </section>

                            <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                <h3 className={"!font-normal"}>Documentos</h3>

                                <div className={"grid grid-cols-3 gap-2"}>
                                    <p>RG: <span className={"info"}>{individual?.rg ?? ""}</span></p>
                                    <p>Emissor: <span className={"info"}>{individual?.emitterRg ?? ""}</span></p>
                                    <p>RG criminal: <span className={"info"}>{individual?.criminalRg ?? ""}</span></p>
                                    <p>CNH: <span className={"info"}>{individual?.cnh ?? ""}</span></p>
                                    <p>Categoria CNH: <span className={"info"}>{individual?.cnhCategories?.join(", ")}</span></p>
                                    <p>Vencimento do exame médico: <span className={"info"}>{individual?.medicalExamDueDate ?? ""}</span></p>
                                    <p>Outro documento: <span className={"info"}>{individual?.otherDocument ?? ""}</span></p>
                                    <p className={"col-span-2"}>Número: <span className={"info"}>{individual?.otherDocumentValue ?? ""}</span></p>
                                    <p>Encaminhamentos: <span className={"info"}>{individual.referral?.join(", ") ?? ""}</span></p>
                                    <p>Data de nasc.: <span className={"info"}>{individual.dtBirth ?? ""}</span></p>
                                    <p>Sexo: <span className={"info"}>{individual.sex ? (BoKeys[individual.sex] ?? individual.sex) : ''}</span></p>
                                    <p>Telefone: <span className={"info"}>{individual.phone ?? ""}</span></p>
                                    <p>Email: <span className={"info"}>{individual.email ?? ''}</span></p>
                                    <p className={"col-span-2"}>Mãe: <span className={"info"}>{individual.mother ?? ""}</span></p>
                                    <p className={"col-span-2"}>Pai: <span className={"info"}>{individual.father ?? ""}</span></p>
                                </div>
                            </section>

                            <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                <h3 className={"!font-normal"}>Informações gerais</h3>

                                <div className={"grid grid-cols-3 gap-2"}>
                                    <p>Estado civil: <span className={"info"}>{individual.maritalStatus ? (BoKeys[individual.maritalStatus] ?? individual.maritalStatus) : ''}</span></p>
                                    <p>Nacionalidade: <span className={"info"}>{individual?.nationality ?? ""}</span></p>
                                    <p>Naturalidade: <span className={"info"}>{individual?.cityOfBirth ?? ""}</span></p>
                                    <p>Nome da mãe: <span className={"info"}>{individual.mother ?? ""}</span></p>
                                    <p>Nome do pai: <span className={"info"}>{individual.father ?? ""}</span></p>
                                    <p>E-mail: <span className={"info"}>{individual.email ?? ''}</span></p>
                                    <p>Telefone: <span className={"info"}>{individual.phone ?? ""}</span></p>
                                    {individual.phoneProvider && <p>Operadora (Telefone): <span className={"info"}>{individual.phoneProvider}</span></p>}
                                </div>
                            </section>

                            <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                <h3 className={"!font-normal"}>Características</h3>

                                <div className={"grid grid-cols-3 gap-2"}>
                                    <p>Altura (cm): <span className={"info"}>{individual.height ? `${individual.height} cm` : ''}</span></p>
                                    <p>Cútis: <span className={"info"}>{individual.color ?? ''}</span></p>
                                    <p>Compleição: <span className={"info"}>{individual.bodyShape ?? ''}</span></p>
                                    <p>Cor dos olhos: <span className={"info"}>{individual.eyes ?? ''}</span></p>
                                    <p>Tipo dos cabelos: <span className={"info"}>{individual.hairType ?? ''}</span></p>
                                    <p>Cor dos cabelos: <span className={"info"}>{individual.hairColor ?? ''}</span></p>
                                    <p>Comprimento dos cabelos: <span className={"info"}>{individual.hairSize ?? ''}</span></p>
                                    <p className={"col-span-2"}>Ferimento/Lesão: <span className={"info"}>{individual.injury ?? ''}</span></p>
                                </div>
                            </section>

                            {individualRegions.length > 0 &&
                                <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                    <h3 className={"!font-normal"}>Outras características</h3>
                                    <div className={"space-y-1"}>
                                        {individualRegions.map((region, regionIndex) =>
                                            <div key={region.itemId ?? regionIndex} className={"grid grid-cols-3 gap-2"}>
                                                {region.local && <p>Local: <span className={"info"}>{region.local}</span></p>}
                                                <p>Tipo: <span className={"info"}>{region.type ?? ""}</span></p>
                                                <p className={region.local ? "" : "col-span-2"}>Descrição: <span className={"info"}>{region.description ?? ""}</span></p>
                                            </div>
                                        )}
                                    </div>
                                </section>
                            }

                            <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                <h3 className={"!font-normal"}>Endereço</h3>

                                <div className={"grid grid-cols-3 gap-2"}>
                                    <p>Situação de rua: <span className={"info"}>{individual?.homeless ? "Sim" : "Não"}</span></p>
                                    <p>Logradouro: <span className={"info"}>{individual?.address?.address ?? ""}</span></p>
                                    <p>Número: <span className={"info"}>{individual?.address?.number ?? ""}</span></p>
                                    <p>Bairro: <span className={"info"}>{individual?.address?.neighborhood ?? ""}</span></p>
                                    <p>Município: <span className={"info"}>{individual?.address?.city ?? ""}</span></p>
                                    <p>Complemento: <span className={"info"}>{individual?.address?.complement ?? ""}</span></p>
                                    <p>CEP: <span className={"info"}>{individual?.address?.zipCode ?? ""}</span></p>
                                    {individual?.address?.date && <p>Data: <span className={"info"}>{projectUtils.formatDate(individual.address.date, "DD/MM/YYYY")}</span></p>}
                                    {individual?.address?.lat !== null && individual?.address?.lat !== undefined && (
                                        <p>Latitude: <span className={"info"}>{individual.address.lat}</span></p>
                                    )}
                                    {individual?.address?.lng !== null && individual?.address?.lng !== undefined && (
                                        <p>Longitude: <span className={"info"}>{individual.address.lng}</span></p>
                                    )}
                                </div>
                            </section>

                            <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                <h3 className={"!font-normal"}>Comercial</h3>

                                <div className={"grid grid-cols-3 gap-2"}>
                                    <p>Empresa: <span className={"info"}>{individual?.companyName ?? ""}</span></p>
                                    <p>Telefone: <span className={"info"}>{individual?.companyPhone ?? ""}</span></p>
                                    <p>Logradouro: <span className={"info"}>{individual?.companyAddress?.address ?? ""}</span></p>
                                    <p>Número: <span className={"info"}>{individual?.companyAddress?.number ?? ""}</span></p>
                                    <p>Bairro: <span className={"info"}>{individual?.companyAddress?.neighborhood ?? ""}</span></p>
                                    <p>Município: <span className={"info"}>{individual?.companyAddress?.city ?? ""}</span></p>
                                    <p>Complemento: <span className={"info"}>{individual?.companyAddress?.complement ?? ""}</span></p>
                                    <p>CEP: <span className={"info"}>{individual?.companyAddress?.zipCode ?? ""}</span></p>
                                </div>
                            </section>

                            <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                <h3 className={"!font-normal"}>Outras informações</h3>

                                <div className={"grid grid-cols-3 gap-2"}>
                                    <p>Grau de instrução: <span className={"info"}>{individual.educationDegree ?? ''}</span></p>
                                    <p>Profissão: <span className={"info"}>{individual.occupation ?? ''}</span></p>
                                    <p>Função no crime: <span className={"info"}>{individual.roleCrime ?? ''}</span></p>
                                    <p>Situação: <span className={"info"}>{individual.situation ?? ''}</span></p>
                                    <p className={"col-span-2"}>Rede social: <span className={"info"}>{individual.socialNetwork ?? ''}</span></p>
                                    <p className={"col-span-3"}>Informações do indivíduo: <span className={"info whitespace-pre-line break-words"}>{individual.information ?? ''}</span></p>
                                </div>
                            </section>

                            {individual.articles?.length > 0 &&
                                <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                    <h3 className={"!font-normal"}>Artigos indiciados</h3>
                                    <div className={"space-y-1"}>
                                        {individual.articles.map((article, articleIndex) => {
                                            const articleAttach = individual.attachs?.find((attach) => attach.attachId === article.attachId);
                                            const articleAttachWithUrl = articleAttach ? {
                                                ...articleAttach,
                                                url: `${verifySyncUrl(articleAttach.attachId)}/${articleAttach.attachId}`,
                                            } : null;
                                            return <div key={article.itemId ?? articleIndex} className={"grid grid-cols-4 gap-2"}>
                                                <p>Data: <span className={"info"}>{article.date ? projectUtils.formatDate(article.date, "DD/MM/YYYY") : ""}</span></p>
                                                <p>Boletim: <span className={"info"}>{article.bo ?? ""}</span></p>
                                                <p>Natureza: <span className={"info"}>{article.article ?? ""}</span></p>
                                                <p>Anexo: <span className={"info"}>{article.attachId ? "Sim" : "Não"}</span>
                                                    {articleAttachWithUrl && <button type={"button"} className={"ml-2 cursor-pointer print:hidden"}
                                                                               title={"Visualizar anexo"}
                                                                               onClick={() => openModal(articleAttachWithUrl)}>
                                                        <FaExternalLinkAlt/>
                                                    </button>}
                                                </p>
                                            </div>;
                                        })}
                                    </div>
                                </section>
                            }

                            <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                <h3 className={"!font-normal"}>Versão do indivíduo</h3>

                                <div>
                                    <p className={"whitespace-pre-line"} style={{width: "100%", textAlign: "justify"}}>{ version }</p>
                                    {
                                        biggestItemId > 0 &&
                                        <div
                                            title="Histórico de versões do indivíduo"
                                            className={"print:hidden justify-items-end cursor-pointer"}
                                            onClick={() => setModalVersion({open: true, index: index})}
                                        >
                                            <FaHistory size={24}/>
                                        </div>
                                    }
                                </div>
                            </section>

                            <section className={"space-y-2 justify-self-center" + (hideData ? " print:hidden" : "")}>
                                <h3 className={"!font-normal text-center"}>Assinatura</h3>

                                {
                                    signOption && signOption !== "SIGNATURE" ?
                                        <span className={"info"}>{BoKeys[signOption] ?? signOption}</span> :
                                        signatureId ?
                                            <div className={"image"}>
                                                <img src={urlAttach + "/" + signatureId} alt={""}/>
                                            </div>
                                            : <span className={"info"}></span>
                                }
                            </section>

                            {photoAttaches.length > 0 &&
                                <section className={"space-y-2" + (hideData ? " print:hidden" : "")}>
                                    <h3 className={"!font-normal"}>Fotos</h3>
                                    <BoShowAttachmentGallery
                                        attachments={photoAttaches}
                                        verifySyncUrl={verifySyncUrl}
                                        titleMaker={(attachment) => attachment?.name ?? "Anexo"}
                                    />
                                </section>
                            }
                        </article>
                    )
                })
            }

            <Modal open={modalVersion.open} setOpen={() => setModalVersion({open: false, index: undefined})} title={`Versão do indivíduo (${modalVersion?.index + 1 })`} className={"w-6/12"} overflow={true}>
                <Modal.Body>
                    {
                        individualList?.[modalVersion.index]?.individualVersion
                            ?.slice(0, -1)
                            .reverse()
                            .map((item) => (
                                <div key={item.itemId} className="space-y-4">
                                    <h3 className="text-center font-semibold">
                                        Versão do indivíduo ({item.itemId})
                                    </h3>

                                    <p className="whitespace-pre-line text-center break-words">
                                        {formatIndividualVersion(item.version)}
                                    </p>

                                    {item.nothingToDeclare ? (
                                        <p className="text-center">
                                            <strong>Nada a declarar</strong>
                                        </p>
                                    ) : item.signOption ? (
                                        item.signOption !== 'SIGNATURE' ? (
                                            <p className="text-center">
                                                <strong>
                                                    {BoKeys[item.signOption] ?? item.signOption}
                                                </strong>
                                            </p>
                                        ) : (
                                            <img
                                                style={{ height: '173px' }}
                                                src={`${urlAttach}/${item.signatureId}`}
                                                alt={`Assinatura da versão ${item.itemId}`}
                                            />
                                        )
                                    ) : null}

                                    <hr className="my-4 opacity-25 text-[#676a6c]" />
                                </div>
                            ))
                    }
                </Modal.Body>
            </Modal>
        </>
    );
};

export default BoShowIndividuals;
